/* global window, document */

import videojs from 'video.js';
import get from 'lodash/get';

import attachAutoplayEvents from './autoplay';
import attachRecommendationEvents from './recommendations';
import { attachStickyPlayerEvents } from './sticky';
import { setQuartileEvents } from './events/quartiles';
import attachAdMarkersEvents from './adMarkers';

export const eventsToQueue = [
  'ads-ima-adBreakReady', // google.ima.AdEvent.Type.AD_BREAK_READY
  'ads-ima-adCanPlay', // google.ima.AdEvent.Type.AD_CAN_PLAY
  'ads-ima-adMetadata', // google.ima.AdEvent.Type.AD_METADATA
  'ads-ima-allAdsCompleted', // google.ima.AdEvent.Type.ALL_ADS_COMPLETED
  'ads-ima-click', // google.ima.AdEvent.Type.CLICK
  'ads-ima-complete', // google.ima.AdEvent.Type.COMPLETE
  'ads-ima-contentPauseRequested', // google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED
  'ads-ima-contentResumeRequested', // google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED
  'ads-ima-durationChange', // google.ima.AdEvent.Type.DURATION_CHANGE
  'ads-ima-expandedChanged', // google.ima.AdEvent.Type.EXPANDED_CHANGED
  'ads-ima-firstquartile', // google.ima.AdEvent.Type.FIRST_QUARTILE
  'ads-ima-impression', // google.ima.AdEvent.Type.IMPRESSION
  'ads-ima-interaction', // google.ima.AdEvent.Type.INTERACTION
  'ads-ima-linearChanged', // google.ima.AdEvent.Type.LINEAR_CHANGED
  'ads-ima-loaded', // google.ima.AdEvent.Type.LOADED
  'ads-ima-log', // google.ima.AdEvent.Type.LOG
  'ads-ima-midpoint', // google.ima.AdEvent.Type.MIDPOINT
  'ads-ima-pause', // google.ima.AdEvent.Type.PAUSED
  'ads-ima-resume', // google.ima.AdEvent.Type.RESUMED
  'ads-ima-skippableStateChanged', // google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED
  'ads-ima-skip', // google.ima.AdEvent.Type.SKIPPED
  'ads-ima-start', // google.ima.AdEvent.Type.STARTED
  'ads-ima-thirdquartile', // google.ima.AdEvent.Type.THIRD_QUARTILE
  'ads-ima-userClose', // google.ima.AdEvent.Type.USER_CLOSE
  'ads-ima-viewable_impression', // google.ima.AdEvent.Type.VIEWABLE_IMPRESSION
  'ads-ima-volumeChange', // google.ima.AdEvent.Type.VOLUME_CHANGED
  'ads-ima-mute', // google.ima.AdEvent.Type.VOLUME_MUTED
  'ads-ima-adError', // google.ima.AdErrorEvent.Type.AD_ERROR
  'ads-ima-adRequest',

  'error',
  'adserror',
  'readyforpreroll',
  'adsready',
  'ads-ad-started',
  'view',
  'play',
  'pause',
  'played-q0',
  'played-q1',
  'played-q2',
  'played-q3',
  'played-q4',
  'ended',
  'contentended',
  'volumechange',
  'fullscreenchange',
  'autoplayStatus',
];

/**
 * Triggers global event 'hearstPlayer:events:ready'.
 */
function triggerGlobalPlayerEvent() {
  const evt = document.createEvent('CustomEvent');
  evt.initCustomEvent('hearstPlayer:events:ready', false, false, null);
  window.dispatchEvent(evt);
}

/**
 * Returns player error message if present, empty string otherwise.
 * @param {Object} player - videojs player.
 * @return {string}
 */
function getErrorMessage(player) {
  const error = player.error();
  if (error) {
    const message = `${error.code}: ${error.message}`;
    return message;
  }
  return '';
}

/**
 * Returns an object with current player settings to be attached in events.
 * @param {Object} Encore - Encore video player instance.
 * @return {Object}
 */
function getPlayerData(encore) {
  return {
    type: 'video.js',
    duration: Math.floor(encore.player.duration()),
    currentTime: Math.floor(encore.player.currentTime()),
    muted: encore.player.muted(),
    volume: encore.player.volume(),
    autoplay: encore.settings.canAutoplay && encore.shouldAutoplay,
    isFullscreen: encore.player.isFullscreen(),
    width: Math.floor(encore.player.currentWidth()),
    height: Math.floor(encore.player.currentHeight()),
    version: `video.js ${videojs.VERSION}`,
    error: getErrorMessage(encore.player),
  };
}

/**
 * Push events into the global event queue,
 * so that they can be processed at a later time.
 * @param {string} eventName - the name of the event to be fired
 * @param {Object} Encore - Encore video player instance.
 * @param {Object} eventData - optional event data.
 * @param {Object} adEventData - optional ad event data.
 */
function queuePlayerEvent(eventName, encore, eventData, adEventData) {
  if (encore.eventTracker.shouldSkipEvent(eventName, encore.player, encore.source)) {
    return;
  }

  const event = {
    type: eventName,
    embed: encore.source.attributes,
    player: getPlayerData(encore),
    event: eventData,
    adEvent: adEventData,
    adTagUrl: encore.source.adTagUrl,
    timestamp: Date.now(),
  };

  window.hearstPlayer.events.push(event);

  triggerGlobalPlayerEvent();
}

/**
 * Hide big play button.
 * @param {Object} Encore - Encore video player instance.
 */
function hideBigPlayButton(encore) {
  const bpb = encore.el.querySelector('.vjs-big-play-button');

  if (bpb) {
    bpb.style.visibility = 'hidden';
  }
}

/**
 * Show big play button.
 * @param {Object} Encore - Encore video player instance.
 */
function showBigPlayButton(encore) {
  const bpb = encore.el.querySelector('.vjs-big-play-button');

  if (bpb) {
    bpb.style.visibility = '';
  }
}

/**
 * Display ad container information.
 * @param {Object} Encore - Encore video player instance.
 */
function adVideoElInfo(encore) {
  const adVideoEl = encore.el.querySelector('.ima-ad-container video');

  if (adVideoEl) {
    const width = adVideoEl.style.width.replace(/\s+/g, '');
    const height = adVideoEl.style.height.replace(/\s+/g, '');

    videojs.log(`${encore.playerElementId} adVideoEl [${width}][${height}]`);

    if (width === '0' || width === '0px' || width === '0%') {
      adVideoEl.style.width = '100%';
      videojs.log(`${encore.playerElementId} adVideoEl width override [${width}][100%]`);
    }

    if (height === '0' || height === '0px' || height === '0%') {
      adVideoEl.style.height = '100%';
      videojs.log(`${encore.playerElementId} adVideoEl height override [${height}][100%]`);
    }
  }
}

/**
 * Display ad pod information.
 * @param {Object} Encore - Encore video player instance.
 * @param {Object} eventData - optional event data.
 * @param {Object} adEventData - optional ad event data.
 */
function adPodInfo(encore, eventData, adEventData) {
  if (!eventData || !adEventData) {
    return;
  }

  const ad = adEventData.getAd();
  const pod = ad.getAdPodInfo();

  videojs.log(`${encore.playerElementId} AdInfo [${ad.getAdId()}][${ad.getCreativeId()}][${ad.getTitle()}]`);
  videojs.log(`${encore.playerElementId} AdPod [${pod.getAdPosition()}]/[${pod.getTotalAds()}]`);
}

/**
 * Return ad cue points.
 * @param {Object} Encore - Encore video player instance.
 * @return {Array}
 */
function getCuePoints(encore) {
  const adsManager = encore.player.ima.getAdsManager();
  if (!adsManager) {
    return [];
  }

  const cuePoints = adsManager.getCuePoints();
  if (!cuePoints.length) {
    return [];
  }

  return cuePoints;
}

/**
 * Ensure playback resumes after ad break is completed.
 * @param {Object} Encore - Encore video player instance.
 * @param {Object} eventData - optional event data.
 * @param {Object} adEventData - optional ad event data.
 */
function ensurePlayback(encore, eventData, adEventData) {
  if (!eventData || !adEventData) {
    return;
  }

  const ad = adEventData.getAd();
  const pod = ad.getAdPodInfo();
  const cuePoints = getCuePoints(encore);

  const isPreRollOnly = (cuePoints.length === 1 && get(cuePoints, '0') === 0) || !cuePoints.length;
  const isLastAdInPod = pod.getAdPosition() === pod.getTotalAds();

  if (isPreRollOnly || isLastAdInPod) {
    videojs.log(`${encore.playerElementId} AdBreak [${pod.getPodIndex() + 1}]/[${cuePoints.length}]`);
    encore.player.autoplay(true);
    encore.player.play();
  }
}

/**
 * Bind all the necessary player events to the queue handler.
 * @param {Object} Encore - Encore video player instance.
 */
export function attachEvents(encore) {
  eventsToQueue.forEach(name => {
    encore.player.on(name, queuePlayerEvent.bind(this,
      name,
      encore));
  });

  if (encore.source.isPlaylist) {
    encore.player.one('play', () => {
      encore.player.autoplay(true);
    });

    encore.player.on('played-q4', () => {
      encore.player.playlist.next();
    });
  }

  encore.player.on('ads-ima-start', adVideoElInfo.bind(this, encore));
  encore.player.on('ads-ima-start', adPodInfo.bind(this, encore));
  encore.player.on('ads-ima-skip', ensurePlayback.bind(this, encore));
  encore.player.on('ads-ima-contentResumeRequested', ensurePlayback.bind(this, encore));

  encore.player.on('ads-ima-contentPauseRequested', hideBigPlayButton.bind(this, encore));
  encore.player.on('ads-ima-contentResumeRequested', showBigPlayButton.bind(this, encore));
  encore.player.on('ads-ima-contentPauseRequested', () => videojs.log(`${encore.playerElementId} ads-ima-contentPauseRequested`));
  encore.player.on('ads-ima-contentResumeRequested', () => videojs.log(`${encore.playerElementId} ads-ima-contentResumeRequested`));
  encore.player.on('ads-ima-start', () => videojs.log(`${encore.playerElementId} ads-ima-start`));
  encore.player.on('ads-ima-complete', () => videojs.log(`${encore.playerElementId} ads-ima-complete`));
  encore.player.on('inViewPlay', () => videojs.log(`${encore.playerElementId} inViewPlay`));
  encore.player.on('contentended', () => videojs.log(`${encore.playerElementId} contentended`));

  encore.player.one('error', () => {
    setTimeout(() => encore.player.bigPlayButton.hide(), 100);
  });

  setQuartileEvents(encore.player);

  attachAutoplayEvents(encore);

  attachStickyPlayerEvents(encore);

  attachRecommendationEvents(encore);

  attachAdMarkersEvents(encore);
}
